// ─── DeviceTrackingPage.jsx ───────────────────────────────────
import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { MapPin, Search, Navigation, Clock, Wifi } from 'lucide-react';

export const DeviceTrackingPage = () => {
  const [devices, setDevices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) { setDevices(r.data); setFiltered(r.data); }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setFiltered(devices.filter(d => !searchQuery || d.deviceName?.toLowerCase().includes(searchQuery.toLowerCase()) || d.assignedTo?.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [searchQuery, devices]);

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#0ea5e920', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    layout: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: '16px', height: '560px' },
    listPanel: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    listHeader: { padding: '14px 16px', borderBottom: '1px solid #e2e8f0' },
    searchWrap: { position: 'relative' },
    searchIn: { width: '100%', padding: '8px 8px 8px 34px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
    searchIco: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
    listBody: { overflowY: 'auto', flex: 1 },
    deviceItem: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' },
    mapPanel: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' },
    mapPlaceholder: { flex: 1, background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #e0f7fa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' },
    detailCard: { position: 'absolute', bottom: '16px', left: '16px', backgroundColor: '#fff', borderRadius: '10px', padding: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '220px' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><MapPin size={22} color="#0ea5e9" /></div>
          <div><h1 style={s.title}>Device Tracking</h1><div style={s.subtitle}>{devices.filter(d => d.location).length} devices with location</div></div>
        </div>
      </div>
      <div style={s.layout}>
        {/* Device List */}
        <div style={s.listPanel}>
          <div style={s.listHeader}>
            <div style={s.searchWrap}>
              <Search size={14} style={s.searchIco} />
              <input type="text" placeholder="Search devices..." style={s.searchIn} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
          </div>
          <div style={s.listBody}>
            {loading ? <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading...</div> : filtered.map(dev => (
              <div key={dev.id} style={{ ...s.deviceItem, backgroundColor: selected?.id === dev.id ? '#f0f9ff' : 'transparent' }}
                onClick={() => setSelected(dev)}
                onMouseEnter={e => { if (selected?.id !== dev.id) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                onMouseLeave={e => { if (selected?.id !== dev.id) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px', color: '#1e293b' }}>{dev.deviceName}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{dev.assignedTo || 'Unassigned'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dev.getDaysSinceLastSeen() === 0 ? '#10b981' : dev.getDaysSinceLastSeen() < 3 ? '#f59e0b' : '#ef4444', marginLeft: 'auto', marginBottom: '3px' }} />
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>{dev.getDaysSinceLastSeen()}d ago</div>
                  </div>
                </div>
                {dev.location && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '11px', color: '#0ea5e9' }}><MapPin size={10} />{dev.location}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Map Panel */}
        <div style={s.mapPanel}>
          <div style={s.mapPlaceholder}>
            <Navigation size={48} color="#0ea5e9" strokeWidth={1.5} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#0ea5e9' }}>Map View</div>
            <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', maxWidth: '240px' }}>
              Integrate Google Maps or Mapbox here.<br />Device GPS coordinates come from <code style={{ backgroundColor: '#e0f2fe', padding: '1px 5px', borderRadius: '4px', fontSize: '11px' }}>device.location</code>
            </div>
          </div>
          {selected && (
            <div style={s.detailCard}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', marginBottom: '8px' }}>{selected.deviceName}</div>
              {[['User', selected.assignedTo || '—'], ['Location', selected.location || 'Unknown'], ['IP', selected.ipAddress || '—'], ['Last Seen', `${selected.getDaysSinceLastSeen()} days ago`]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>{l}</span>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#1e293b' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceTrackingPage;